import { Skeleton } from "@mantine/core";
import { type ReactNode, useId, useState } from "react";

import { useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { GenerationDetailModal } from "../../../platform/jobs/detail/GenerationDetailModal";
import { GENERATIONS_PAGE, useJobList } from "../../../platform/jobs/job-list";
import { modelTitle } from "../../../platform/jobs/model-name";
import { useStreamStatus } from "../../../platform/stream-status";
import home from "../pro-home.module.css";
import { ResultCard } from "../ResultCard";
import classes from "./simple-sections.module.css";

export interface RecentProps {
  teamId: string;
  brandId: string;
  medium: "image" | "video";
  /** Its heading: "Recent videos", "Recent images". */
  title: string;
  /** Its line while the brand has made none. */
  empty: string;
  /** Beside the heading: the section's ways on, "All videos" and the like. */
  links: ReactNode;
}

/**
 * A section's recent work (the prototype's "Recent videos" and "Recent images"): the brand's
 * newest of one medium, the Studio's My Generations read of it (5 a page, its key, Pro's Create
 * page's too), as result cards that open the job's detail. Keyed by its team and brand, so a
 * result opened under one brand never shows over another's (specs ui-perf/simple-videos and
 * ui-perf/simple-images).
 */
export function Recent(props: RecentProps) {
  return <RecentWork key={`${props.teamId} ${props.brandId}`} {...props} />;
}

function RecentWork({ teamId, brandId, medium, title, empty, links }: RecentProps) {
  const id = useId();
  const [open, setOpen] = useState<string | null>(null);
  const jobs = useJobList(teamId, { status: "all", brandId, medium, pageSize: GENERATIONS_PAGE }, useStreamStatus());
  const catalog = useGetCatalog().data;
  const shown = jobs.data?.pages[0]?.items;
  return (
    <section className={classes.section} aria-labelledby={id}>
      <div className={classes.sectionHead}>
        <h2 id={id} className={classes.sectionTitle}>
          {title}
        </h2>
        <span className={classes.links}>{links}</span>
      </div>
      {jobs.isError ? <ErrorAlert error={jobs.error} /> : null}
      {shown === undefined ? (
        jobs.isError ? null : (
          <div className={classes.gallery}>
            {[0, 1, 2].map((each) => (
              <Skeleton key={each} className={home.mediaSkeleton} />
            ))}
          </div>
        )
      ) : shown.length === 0 ? (
        <p className={classes.empty}>{empty}</p>
      ) : (
        <div className={classes.gallery}>
          {shown.map((job) => (
            <ResultCard
              key={job.id}
              job={job}
              model={modelTitle(catalog, job)}
              onLost={() => void jobs.refetch()}
              onOpen={() => setOpen(job.id)}
            />
          ))}
        </div>
      )}
      {open === null ? null : <GenerationDetailModal teamId={teamId} jobId={open} onClose={() => setOpen(null)} />}
    </section>
  );
}
